import { Link, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Carte, CorpsCarte, EnTeteCarte } from "@/composants/ui/carte";
import { Chargement, Erreur, Vide } from "@/composants/ui/etats";
import { Etiquette } from "@/composants/ui/etiquette";
import { LegendeEtapes, Timeline } from "@/composants/timeline";
import { adresseEnLigne } from "@/composants/champs-commande";
import { useChargement } from "@/lib/chargement";
import { dateLongue, euros, nomPersonne, statutLot, statutCommande } from "@/lib/libelles";
import { api, type FicheAffaire } from "@/lib/api";
import { useSession } from "@/session";

/**
 * L'affaire : le dossier d'un client, d'un seul tenant (ADR-0028).
 *
 *     « L'affaire constitue le niveau de regroupement métier et permettra une
 *       vision consolidée du dossier. » — LAB, 18/09 §1
 *
 * La vision consolidée s'arrête où le LOT 1 s'arrête : les commandes, leurs
 * lots, leurs timelines. Les montants agrégés et la marge viendront avec
 * le LOT 8. L'affaire ne porte aucune personne : chaque commande dit son
 * propriétaire, et c'est sur la commande qu'on le transfère (ADR-0032).
 */
export function PageAffaire() {
  const { id = "" } = useParams();
  const session = useSession();
  const identite = session.statut === "connecte" ? session.identite : null;
  const voitLesMontants = identite?.permissions.includes("CONSULTER_MONTANTS") ?? false;

  const { donnees, erreur, enCours, recharger } = useChargement<FicheAffaire>(
    (signal) => api.affaire(id, signal),
    [id],
  );
  const courante = donnees;
  if (erreur) return <Erreur message={erreur} onReessayer={recharger} />;
  if (!courante || enCours) return <Chargement message="Chargement de l'affaire…" />;

  const { affaire, client, commandes } = courante;
  const nombreLots = commandes.reduce((total, c) => total + c.lots.length, 0);

  return (
    <div className="flex flex-col gap-5">
      <Link
        to="/affaires"
        className="inline-flex items-center gap-1.5 text-sm text-encre-douce hover:text-encre"
      >
        <ArrowLeft className="h-4 w-4" aria-hidden />
        Toutes les affaires
      </Link>

      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-mono text-xl font-semibold text-encre">{affaire.numero}</h1>
          <p className="mt-1 text-sm text-encre-douce">
            Affaire de{" "}
            <Link
              to={`/clients/${client.id}`}
              className="font-medium text-encre underline underline-offset-4"
            >
              {client.designation}
            </Link>
            {client.ville ? ` — ${client.ville}` : ""}, ouverte le {dateLongue(affaire.cree_le.slice(0, 10))}
          </p>
        </div>
        <Etiquette>
          {commandes.length} commande{commandes.length > 1 ? "s" : ""}, {nombreLots} lot
          {nombreLots > 1 ? "s" : ""}
        </Etiquette>
      </div>

      {commandes.length === 0 ? (
        <Carte>
          <Vide
            titre="Aucune commande visible"
            detail="Les commandes de cette affaire ne sont pas dans votre périmètre."
          />
        </Carte>
      ) : null}

      {nombreLots > 0 ? <LegendeEtapes /> : null}

      {commandes.map(({ commande, lots }) => (
        <Carte key={commande.id}>
          <EnTeteCarte className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
              <Link
                to={`/commandes/${commande.id}`}
                className="font-mono text-base font-semibold text-encre underline underline-offset-4"
              >
                {commande.numero_commande_client}
              </Link>
              <span className="text-sm text-encre-douce">
                commandée le {dateLongue(commande.date_commande)}
                {voitLesMontants && commande.montant_ht !== null
                  ? ` — ${euros(commande.montant_ht)} HT`
                  : ""}
              </span>
              <span className="text-sm text-encre-douce">
                Propriétaire : {nomPersonne(commande.proprietaire)}
              </span>
            </div>
            <Etiquette ton={commande.statut === "CLOTUREE" ? "succes" : "neutre"}>
              {statutCommande(commande.statut)}
            </Etiquette>
          </EnTeteCarte>
          <CorpsCarte>
            {lots.length === 0 ? (
              <p className="text-sm text-encre-douce">Aucun lot encore découpé.</p>
            ) : (
              <div className="grid gap-4 md:grid-cols-2">
                {lots.map((lot) => (
                  <div key={lot.id} className="rounded-[6px] border border-filet">
                    <div className="flex items-start justify-between gap-3 border-b border-filet px-3 py-2">
                      <div className="min-w-0">
                        <Link
                          to={`/lots/${lot.id}`}
                          className="font-mono text-sm text-encre-douce underline underline-offset-4"
                        >
                          {lot.code_lot}
                        </Link>
                        <p className="font-medium text-encre">{lot.intitule}</p>
                        <p className="text-sm text-encre-douce">
                          {lot.categorie_libelle} — {adresseEnLigne(lot.adresse_realisation)}
                        </p>
                      </div>
                      <Etiquette>{statutLot(lot.statut_affiche)}</Etiquette>
                    </div>
                    <div className="px-3 py-2">
                      <Timeline
                        etapes={lot.etapes}
                        prochaineAction={lot.prochaine_action}
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CorpsCarte>
        </Carte>
      ))}
    </div>
  );
}
